
import { supabase } from "@/integrations/supabase/client";
import { GrowthTask } from "./types";

export const fetchGrowthTasks = async (
  userId: string, 
  brandId: string
): Promise<GrowthTask[]> => {
  // Get ALL squad members for the current user in this brand
  const { data: squadData, error: squadError } = await supabase
    .from('growth_squad')
    .select('id')
    .eq('user_id', userId)
    .eq('brand_id', brandId);

  if (squadError) {
    console.error('Error fetching squad data:', squadError);
    throw squadError;
  }

  // If no squad members, return empty array
  if (!squadData || squadData.length === 0) {
    return [];
  }

  // Extract all squad IDs
  const squadIds = squadData.map(squad => squad.id);

  const { data, error } = await supabase
    .from('growth_tasks')
    .select(`
      *,
      sales_channels!fk_growth_tasks_sales_channel (
        id,
        name
      ),
      growth_sprints!fk_growth_tasks_sprint_id (
        id,
        name
      ),
      brands (
        name,
        slug
      ),
      os_tasks_projects!fk_growth_tasks_project_id (
        id,
        name,
        description,
        icon
      )
    `)
    .eq('brand_id', brandId)
    .in('owner', squadIds)
    .order('created_at', { ascending: false });

  if (error) throw error;
  
  // Batch fetch all unique submitted_by squad members in a single query
  const uniqueSubmittedByIds = [...new Set(
    (data || [])
      .map((task: any) => task.submitted_by)
      .filter(Boolean)
  )];
  
  let squadMap: Record<string, { name: string; alias: string; email: string }> = {};
  
  if (uniqueSubmittedByIds.length > 0) {
    const { data: squadMembers } = await supabase
      .from('growth_squad')
      .select('id, name, alias, email')
      .in('id', uniqueSubmittedByIds);
    
    if (squadMembers) {
      squadMap = squadMembers.reduce((acc, member) => {
        acc[member.id] = { name: member.name, alias: member.alias, email: member.email };
        return acc;
      }, {} as Record<string, { name: string; alias: string; email: string }>);
    }
  }
  
  const tasksWithSquadInfo = (data || []).map((task: any) => ({
    ...task,
    growth_squad: task.submitted_by ? squadMap[task.submitted_by] || null : null,
    sales_channel: task.sales_channels ? {
      id: task.sales_channels.id,
      name: task.sales_channels.name
    } : undefined,
    sprint: task.growth_sprints ? {
      id: task.growth_sprints.id,
      name: task.growth_sprints.name
    } : undefined,
    brands: task.brands ? {
      name: task.brands.name,
      slug: task.brands.slug
    } : undefined,
    status: task.task_status || 'pending'
  }));
  
  return tasksWithSquadInfo;
};

export const deleteGrowthTask = async (taskId: string): Promise<void> => {
  const { error } = await supabase
    .from('growth_tasks')
    .delete()
    .eq('id', taskId);

  if (error) throw error;
};
